import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Stack,
  Paper,
  Divider,
  useTheme,
  alpha,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  LinearProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  EventBusy as EventBusyIcon,
  ErrorOutline as ErrorOutlineIcon,
  SupportAgent as SupportAgentIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import StorefrontIcon from '@mui/icons-material/Storefront';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
// Styled components for enhanced UI elements
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const GlassCard = styled(motion.div)(({ theme, color }) => ({
  position: "relative",
  borderRadius: 16,
  overflow: "hidden",
  // background: alpha(color || theme.palette.background.paper, 0.7),
  backdropFilter: "blur(10px)",
  boxShadow: `0 10px 30px -10px ${alpha(color || "#000", 0.2)}`,
  border: `1px solid ${alpha("#fff", 0.1)}`,
  transition: "all 0.3s ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 20px 40px -15px ${alpha(color || "#000", 0.3)}`,
  },
}));

const AnimatedNumber = ({
  value,
  prefix = "",
  suffix = "",
  duration = 1.5,
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime;
    let animationFrameId;

    const updateValue = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setDisplayValue(Math.floor(progress * value));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(updateValue);
      }
    };

    animationFrameId = requestAnimationFrame(updateValue);

    return () => cancelAnimationFrame(animationFrameId);
  }, [value, duration]);

  return (
    <>
      {prefix}
      {displayValue.toLocaleString()}
      {suffix}
    </>
  );
};

const StatCard = ({
  title,
  count,
  amount,
  icon,
  color,
  gradientEndColor,
  index,
}) => {
  const theme = useTheme();
  const gradientColor = gradientEndColor || alpha(color, 0.7);
  const isHighlighted = index === 0;

  return (
    <GlassCard
      color={isHighlighted ? color : undefined}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      // className='assssss'
      sx={{ height: "100%" }}
    >
      {isHighlighted ? (
        <Box
          sx={{
            background: "#8bdee7c4",
            height: "100%",
            width: "100%",
            position: "absolute",
            zIndex: -1,
          }}
        />
      ) : (
        <Box
          sx={{
            height: "5px",
            width: "100%",
            background: `linear-gradient(to right, ${color}, ${gradientColor})`,
            opacity: "0.4",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
      )}

      <CardContent sx={{ p: 2, height: "8%", position: "relative" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: isHighlighted ? alpha("#fff", 0.2) : alpha(color, 0.1),
                color: isHighlighted ? "#fff" : color,
                width: 42,
                height: 42,
              }}
            >
              {icon}
            </Avatar>
            <Typography
              variant="h6"
              color={isHighlighted ? "white" : "#495a69"}
              fontWeight={600}
              sx={{ letterSpacing: 0.5 }}
              fontSize={"16px"}
            >
              {title}
            </Typography>
          </Box>

          {/* {amount > 0 && (
            <Tooltip title="View details" arrow>
              <IconButton 
                size="small" 
                sx={{ 
                  bgcolor: isHighlighted ? alpha('#fff', 0.15) : alpha(color, 0.08),
                  color: isHighlighted ? '#fff' : color,
                  '&:hover': {
                    bgcolor: isHighlighted ? alpha('#fff', 0.25) : alpha(color, 0.15),
                  }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )} */}
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            my: 1,
            minHeight: 80,
          }}
        >
          <Typography
            variant="h2"
            component={motion.h2}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
            color={isHighlighted ? "white" : "#495a69"}
            fontWeight="bold"
            sx={{
              mb: 1,
              textShadow: isHighlighted ? "0 2px 5px rgba(0,0,0,0.2)" : "none",
            }}
          >
            <AnimatedNumber value={count} />
          </Typography>

          <Paper
            elevation={0}
            component={motion.div}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 3,
              backgroundColor: isHighlighted
                ? alpha("#fff", 0.15)
                : alpha(color, 0.08),
              backdropFilter: "blur(5px)",
              border: isHighlighted
                ? `1px solid ${alpha("#fff", 0.2)}`
                : `1px solid ${alpha(color, 0.2)}`,
            }}
          >
            <Typography
              variant="h6"
              color={isHighlighted ? "white" : "#495a69"}
              fontWeight="600"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <span style={{ fontSize: "1.2rem", marginRight: "4px" }}>₹</span>
              <AnimatedNumber value={amount} />
            </Typography>
          </Paper>
        </Box>
        {/*         
        {amount > 0 && amount < 1000 && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={(amount / 15000) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: isHighlighted 
                  ? alpha('#fff', 0.15) 
                  : alpha(theme.palette.grey[300], 0.8),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: isHighlighted 
                    ? 'white' 
                    : `linear-gradient(to right, ${color}, ${gradientColor})`,
                },
              }}
            />
            <Typography 
              variant="caption" 
              color={isHighlighted ? alpha('#fff', 0.7) : 'text.secondary'}
              sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
            >
              {Math.round((amount / 15000) * 100)}% of goal
            </Typography>
          </Box>
        )} */}

        {isHighlighted && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 0.05, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{
              position: "absolute",
              bottom: -30,
              right: -20,
              fontSize: "180px",
              transform: "rotate(-10deg)",
              color: "#fff",
              zIndex: 0,
            }}
          >
            {icon}
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
};

const StatsDashboard = ({ data }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewType, setViewType] = useState("grid");
  const open = Boolean(anchorEl);
  const [stats, setStats] = useState([]);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {

    let dataList = data.map((item) => {
      if (item.status == "Overall") {
        return({
          ...item, 
          title: "Overall",
        count: item.total_count,
        amount: item.total_amount,
        icon: <TrendingUpIcon />,
        color: "#4361EE",
        gradientEndColor: "#6457fa",
        })
      } else if (item.status == "Completed") {
        return({
          ...item, 
          title: "Completed",
          count: item.total_count,
        amount: item.total_amount,
          icon: <CheckCircleIcon />,
          color: "#0B8d85",
          gradientEndColor: "#0bd99e",
         })
      } else if (item.status == "Pending") {
        return({
          ...item, 
          title: "Pending",
          count: item.total_count,
        amount: item.total_amount,
          icon: <HourglassEmptyIcon />,
          color: "#FFBA0A",
          gradientEndColor: "#ffcf4d",
         })
      } else if (item.status == "Expired") {
        return({
          ...item, 
          title: "Expired",
          count: item.total_count,
        amount: item.total_amount,
          icon: <EventBusyIcon />,
          color: "#FF6370",
          gradientEndColor: "#ff8e98",
         })
      } else if (item.status == "Failed") {
        return({
          ...item, 
          title: "Failed",
          count: item.total_count,
          amount: item.total_amount,
        icon: <ErrorOutlineIcon />,
        color: "#E93445",
        gradientEndColor: "#e95461",
         })
      } else if (item.status == "Agent Raised") {
        return({
          ...item, 
          title: "Agent Raised",
          count: item.total_count,
        amount: item.total_amount,
          icon: <SupportAgentIcon />,
          color: "#28A745",
          gradientEndColor: "#5CD68D",
         })
      } else if (item.status == "Merchant Raised") {
        return({
          ...item, 
          count: item.total_count,
          amount: item.total_amount,
          icon: <StorefrontIcon />,
          title: "Merchant Raised",
          color: "#008080",
          gradientEndColor: "#20C4C4",
         })
      } else if (item.status == "Rejected") {
        return ({
          ...item,
          count: item.total_count,
          amount: item.total_amount,
          icon: <BlockIcon />,
          title: "Rejected",
          color: "#8A2BE2",
          gradientEndColor: "#B580FF",
         })
      } else if (item.status == "Cancelled") {
        return({
          ...item, 
          count: item.total_count,
          amount: item.total_amount,
          icon: <CancelIcon />,
          title: "Cancelled",
          color: "#E63946",
          gradientEndColor: "#FF6B6B",
         })
      }
    });
    setStats(dataList);
  }, [data]);
  // Data for the dashboard

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      sx={{
        minHeight: "50vh",
        // background: 'linear-gradient(135deg, #f5f7ff 0%, #e9eeff 100%)',
        // p: { xs: 2, md: 4 }
      }}
    >
      <Paper
        elevation={0}
        component={motion.div}
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        sx={
          {
            // p: { xs: 2, md: 4 },
            // maxWidth: 1400,
            // mx: 'auto',
            // borderRadius: 4,
            // background: alpha(theme.palette.background.paper, 0.8),
            // backdropFilter: 'blur(10px)',
            // boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
            // border: `1px solid ${alpha('#fff', 0.4)}`,
          }
        }
      >
        <Grid
          container
          spacing={1}
          sx={{ padding: "0px", margin: "0px", gap: "2" }}
        >
          {stats.map((stat, index) => (
            <Grid
              item
              xs={6}
              sm={6}
              md={4}
              key={index}
              sx={{ padding: "0px", gap: "0", height: "220px" }}
            >
              <StatCard {...stat} index={index} />
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default StatsDashboard;
